import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowLeft, X } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'pk_test_dummy');

function resolvePackageId(routePackageId, search, packageMap) {
  const searchParams = new URLSearchParams(search);
  const queryPackageId = searchParams.get('package');
  const requestedPackageId = routePackageId || queryPackageId || 'postcard';

  if (packageMap[requestedPackageId]) {
    return requestedPackageId;
  }

  if (packageMap.postcard) {
    return 'postcard';
  }

  return Object.keys(packageMap)[0] || 'postcard';
}

export default function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { packageId } = useParams();
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [packagesError, setPackagesError] = useState('');
  const [selectedPackage, setSelectedPackage] = useState('postcard');
  const [step, setStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistData, setWaitlistData] = useState(null);
  const [voucher, setVoucher] = useState(null);
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [voucherMessage, setVoucherMessage] = useState('');
  const packageMap = useMemo(
    () => Object.fromEntries(packages.map((pkg) => [pkg.id, pkg])),
    [packages],
  );

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'DE',
  });

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await fetch('/api/packages');
        if (!response.ok) {
          throw new Error('Failed to load packages');
        }

        const data = await response.json();
        const paidPackages = (data.packages || [])
          .filter((pkg) => !pkg.isDemoPackage && pkg.price > 0)
          .sort((a, b) => a.price - b.price);

        setPackages(paidPackages);
        setPackagesError('');
      } catch (error) {
        console.error('Failed to load checkout packages:', error);
        setPackagesError('Pakete konnten nicht geladen werden.');
      } finally {
        setPackagesLoading(false);
      }
    };

    fetchPackages();
  }, []);

  useEffect(() => {
    if (packagesLoading || packages.length === 0) {
      return;
    }

    const nextPackageId = resolvePackageId(packageId, location.search, packageMap);
    setSelectedPackage(nextPackageId);
  }, [packageId, location.search, packageMap, packages, packagesLoading]);

  const handlePackageChange = (nextPackageId) => {
    setSelectedPackage(nextPackageId);
    navigate(`/checkout?package=${nextPackageId}`, { replace: true });
  };

  const pkg = packageMap[selectedPackage];

  if (packagesLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 text-white">
        <div className="mx-auto flex min-h-[60vh] max-w-4xl items-center justify-center">
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-purple-500"></div>
            <p className="text-slate-300">Pakete werden geladen...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 text-white">
        <div className="mx-auto max-w-4xl">
          <button
            onClick={() => navigate('/#pricing')}
            className="mb-8 flex items-center gap-2 text-purple-300 transition hover:text-purple-200"
          >
            <ArrowLeft size={20} />
            Zurueck
          </button>

          <div className="rounded-lg border border-red-500/30 bg-slate-800/50 p-6 text-center">
            <h1 className="mb-3 text-3xl font-bold">Checkout</h1>
            <p className="text-slate-300">
              {packagesError || 'Das gewuenschte Paket konnte nicht geladen werden.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const basePrice = pkg.price;
  const discountValue = voucher ? voucher.discount_value : 0;
  const total = ((basePrice - discountValue) * 1.19).toFixed(2);

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const applyVoucher = async () => {
    if (!voucherCode.trim()) {
      setVoucherMessage('Bitte gib einen Gutschein-Code ein.');
      return;
    }

    setVoucherLoading(true);
    setVoucherMessage('');

    try {
      const res = await axios.post('/api/checkout/apply-voucher', {
        code: voucherCode.trim(),
        basePrice,
      });

      if (res.data.success) {
        setVoucher(res.data);
        setVoucherMessage(
          `Gutschein ${res.data.code} angewendet. EUR ${res.data.discount_value.toFixed(2)} Rabatt.`,
        );
        setVoucherCode('');
      }
    } catch (error) {
      setVoucherMessage(error.response?.data?.error || 'Gutschein ungueltig oder abgelaufen.');
      setVoucher(null);
    } finally {
      setVoucherLoading(false);
    }
  };

  const handleContinue = () => {
    if (!formData.email || !formData.firstName || !formData.lastName || !formData.address) {
      alert('Bitte fuelle alle erforderlichen Felder aus.');
      return;
    }
    setStep(2);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 text-white">
      <div className="mx-auto max-w-4xl">
        <button
          onClick={() => navigate('/#pricing')}
          className="mb-8 flex items-center gap-2 text-purple-300 transition hover:text-purple-200"
        >
          <ArrowLeft size={20} />
          Zurueck
        </button>

        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 text-4xl font-bold"
        >
          Checkout
        </motion.h1>

        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-8"
              >
                <div className="rounded-lg border border-purple-500/30 bg-slate-800/50 p-6">
                  <h2 className="mb-4 text-2xl font-bold">Deine Daten</h2>
                  <div className="space-y-4">
                    <input
                      type="email"
                      name="email"
                      placeholder="E-Mail-Adresse"
                      value={formData.email}
                      onChange={handleFormChange}
                      className="w-full rounded-lg border border-slate-600 bg-slate-700/50 px-4 py-3 transition focus:border-purple-500 focus:outline-none"
                      required
                    />

                    <div className="grid gap-4 md:grid-cols-2">
                      <input
                        type="text"
                        name="firstName"
                        placeholder="Vorname"
                        value={formData.firstName}
                        onChange={handleFormChange}
                        className="rounded-lg border border-slate-600 bg-slate-700/50 px-4 py-3 transition focus:border-purple-500 focus:outline-none"
                        required
                      />
                      <input
                        type="text"
                        name="lastName"
                        placeholder="Nachname"
                        value={formData.lastName}
                        onChange={handleFormChange}
                        className="rounded-lg border border-slate-600 bg-slate-700/50 px-4 py-3 transition focus:border-purple-500 focus:outline-none"
                        required
                      />
                    </div>

                    <input
                      type="text"
                      name="address"
                      placeholder="Strasse und Hausnummer"
                      value={formData.address}
                      onChange={handleFormChange}
                      className="w-full rounded-lg border border-slate-600 bg-slate-700/50 px-4 py-3 transition focus:border-purple-500 focus:outline-none"
                      required
                    />

                    <div className="grid gap-4 md:grid-cols-2">
                      <input
                        type="text"
                        name="postalCode"
                        placeholder="Postleitzahl"
                        value={formData.postalCode}
                        onChange={handleFormChange}
                        className="rounded-lg border border-slate-600 bg-slate-700/50 px-4 py-3 transition focus:border-purple-500 focus:outline-none"
                      />
                      <input
                        type="text"
                        name="city"
                        placeholder="Stadt"
                        value={formData.city}
                        onChange={handleFormChange}
                        className="rounded-lg border border-slate-600 bg-slate-700/50 px-4 py-3 transition focus:border-purple-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleContinue}
                    className="mt-6 w-full rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 py-3 font-bold transition-all hover:from-purple-700 hover:to-pink-700"
                  >
                    Zahlungsmethode waehlen →
                  </button>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-8"
              >
                <div className="rounded-lg border border-purple-500/30 bg-slate-800/50 p-6">
                  <h2 className="mb-4 text-2xl font-bold">Paket waehlen</h2>
                  <p className="mb-4 text-sm text-slate-400">
                    Du kannst dein Paket hier noch wechseln, falls du mehr Funktionen moechtest.
                  </p>

                  <div className="grid grid-cols-2 gap-3">
                    {packages.map((packageItem) => (
                      <button
                        key={packageItem.id}
                        onClick={() => handlePackageChange(packageItem.id)}
                        className={`rounded-lg border-2 p-3 text-left transition-all ${
                          selectedPackage === packageItem.id
                            ? 'border-purple-500 bg-purple-600/20'
                            : 'border-slate-700 bg-slate-700/30 hover:border-slate-600'
                        }`}
                      >
                        <div className="text-sm font-bold">{packageItem.name}</div>
                        <div className="text-xs text-slate-400">EUR {packageItem.price}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-lg border border-purple-500/30 bg-slate-800/50 p-6">
                  <h2 className="mb-4 text-2xl font-bold">Zahlungsart</h2>

                  <div className="space-y-3">
                    <button
                      onClick={() => setPaymentMethod('stripe')}
                      className={`flex w-full items-center gap-4 rounded-lg border-2 p-4 text-left transition-all ${
                        paymentMethod === 'stripe'
                          ? 'border-purple-500 bg-purple-600/20'
                          : 'border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      <div
                        className="flex h-6 w-6 items-center justify-center rounded-full border-2"
                        style={{ borderColor: paymentMethod === 'stripe' ? '#a855f7' : '#64748b' }}
                      >
                        {paymentMethod === 'stripe' && <div className="h-3 w-3 rounded-full bg-purple-500" />}
                      </div>
                      <div>
                        <div className="font-bold">Kreditkarte</div>
                        <div className="text-sm text-slate-400">Visa, Mastercard, American Express</div>
                      </div>
                    </button>

                    <button
                      onClick={() => setPaymentMethod('paypal')}
                      className={`flex w-full items-center gap-4 rounded-lg border-2 p-4 text-left transition-all ${
                        paymentMethod === 'paypal'
                          ? 'border-purple-500 bg-purple-600/20'
                          : 'border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      <div
                        className="flex h-6 w-6 items-center justify-center rounded-full border-2"
                        style={{ borderColor: paymentMethod === 'paypal' ? '#a855f7' : '#64748b' }}
                      >
                        {paymentMethod === 'paypal' && <div className="h-3 w-3 rounded-full bg-purple-500" />}
                      </div>
                      <div>
                        <div className="font-bold">PayPal</div>
                        <div className="text-sm text-slate-400">Sicher mit deinem PayPal-Konto</div>
                      </div>
                    </button>
                  </div>

                  <div className="mt-6 grid grid-cols-2 gap-4">
                    <button
                      onClick={() => setStep(1)}
                      className="rounded-lg border-2 border-slate-600 px-6 py-3 transition hover:border-slate-500"
                    >
                      ← Zurueck
                    </button>
                    <button
                      onClick={() => setStep(3)}
                      className="rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 py-3 font-bold transition-all hover:from-purple-700 hover:to-pink-700"
                    >
                      Fortfahren →
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <Elements stripe={stripePromise}>
                <PaymentStep
                  packageId={selectedPackage}
                  pkg={pkg}
                  paymentMethod={paymentMethod}
                  formData={formData}
                  voucher={voucher}
                  basePrice={basePrice}
                  total={total}
                  onBack={() => setStep(2)}
                  onWaitlist={(data) => {
                    setWaitlistData(data);
                    setShowWaitlist(true);
                  }}
                />
              </Elements>
            )}
          </div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="sticky top-8 rounded-lg border border-purple-500/30 bg-slate-800/50 p-6">
              <h3 className="mb-6 text-xl font-bold">Zusammenfassung</h3>

              <div className="mb-6 space-y-3 border-b border-slate-700 pb-6">
                <div className="text-lg font-bold">{pkg.name}</div>
                <div className="text-sm text-slate-400">{pkg.description}</div>

                <div className="mt-4 space-y-2">
                  {(pkg.features || []).map((feature, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                      <Check size={16} className="text-green-400" />
                      {feature}
                    </div>
                  ))}
                </div>

                {!voucher && (
                  <div className="mt-6 border-t border-slate-700 pt-6">
                    <div className="mb-3 text-sm font-bold">Hast du einen Gutschein?</div>
                    <div className="flex w-full gap-2">
                      <input
                        type="text"
                        value={voucherCode}
                        onChange={(e) => setVoucherCode(e.target.value)}
                        placeholder="Gutschein-Code eingeben"
                        className="min-w-0 flex-1 rounded-lg border border-slate-600 bg-slate-700/50 px-3 py-2 text-sm text-white placeholder-slate-400 transition focus:border-purple-500 focus:outline-none"
                        onKeyDown={(e) => e.key === 'Enter' && applyVoucher()}
                      />
                      <button
                        onClick={applyVoucher}
                        disabled={voucherLoading || !voucherCode.trim()}
                        className="flex-shrink-0 whitespace-nowrap rounded-lg bg-purple-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-purple-700 disabled:opacity-50"
                      >
                        {voucherLoading ? 'Pruefe...' : 'Anwenden'}
                      </button>
                    </div>
                    {voucherMessage && <div className="mt-2 text-xs text-slate-400">{voucherMessage}</div>}
                  </div>
                )}

                {voucher && (
                  <div className="mt-6 border-t border-slate-700 pt-6">
                    <div className="rounded-lg border border-green-500/50 bg-green-900/30 p-3">
                      <div className="text-sm font-bold text-green-300">Gutschein aktiv</div>
                      <div className="mt-1 text-xs text-slate-300">{voucher.code}</div>
                      <button
                        onClick={() => {
                          setVoucher(null);
                          setVoucherMessage('');
                        }}
                        className="mt-2 text-xs text-slate-400 underline hover:text-slate-200"
                      >
                        Gutschein entfernen
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-6 space-y-2">
                <div className="flex justify-between text-slate-400">
                  <span>Subtotal</span>
                  <span>EUR {pkg.price.toFixed(2)}</span>
                </div>
                {voucher && (
                  <div className="flex justify-between text-sm text-green-400">
                    <span>Rabatt ({voucher.discount_type === 'percent' ? `${voucher.discount_percent}%` : 'Pauschal'})</span>
                    <span>-EUR {voucher.discount_value.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm text-slate-400">
                  <span>MwSt. (19%)</span>
                  <span>EUR {((basePrice - discountValue) * 0.19).toFixed(2)}</span>
                </div>
              </div>

              <div className="flex justify-between border-t border-slate-700 pt-4 text-xl font-bold">
                <span>Gesamt</span>
                <span>EUR {total}</span>
              </div>

              <div className="mt-6 rounded-lg bg-slate-700/50 p-4 text-sm text-slate-300">
                Mit Verschluesselung gesichert. Deine Daten sind sicher.
              </div>
            </div>
          </motion.div>
        </div>

        {showWaitlist && (
          <WaitlistModal
            data={waitlistData}
            onClose={() => setShowWaitlist(false)}
            onNavigate={() => {
              navigate('/');
              setShowWaitlist(false);
            }}
          />
        )}
      </div>
    </div>
  );
}

function PaymentStep({ packageId, pkg, paymentMethod, formData, voucher, basePrice, total, onBack, onWaitlist }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  if (paymentMethod === 'stripe') {
    return (
      <StripePaymentForm
        stripe={stripe}
        elements={elements}
        packageId={packageId}
        pkg={pkg}
        formData={formData}
        voucher={voucher}
        basePrice={basePrice}
        total={total}
        loading={loading}
        setLoading={setLoading}
        onBack={onBack}
        onWaitlist={onWaitlist}
      />
    );
  }

  if (paymentMethod === 'paypal') {
    return (
      <PayPalPaymentForm
        packageId={packageId}
        pkg={pkg}
        formData={formData}
        voucher={voucher}
        basePrice={basePrice}
        total={total}
        loading={loading}
        setLoading={setLoading}
        onBack={onBack}
        onWaitlist={onWaitlist}
      />
    );
  }

  return null;
}

function StripePaymentForm({ stripe, elements, packageId, pkg, formData, voucher, basePrice, total, loading, setLoading, onBack, onWaitlist }) {
  const handlePaymentClick = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);

    try {
      const res = await axios.post('/api/payments/pending', {
        packageId,
        amount: basePrice,
        voucherCode: voucher?.code || null,
        voucherDiscount: voucher?.discount_value || 0,
        finalAmount: basePrice - (voucher?.discount_value || 0),
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        address: formData.address,
        city: formData.city,
        postalCode: formData.postalCode,
        paymentMethod: 'stripe',
      });

      onWaitlist({
        email: formData.email,
        firstName: formData.firstName,
        package: pkg.name,
        voucherCode: res.data.voucherCode,
      });
    } catch (error) {
      alert(`Fehler: ${error.message}`);
      setLoading(false);
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="rounded-lg border border-purple-500/30 bg-slate-800/50 p-6">
        <h2 className="mb-4 text-2xl font-bold">Zahlungsdaten</h2>

        <div className="mb-6 space-y-4">
          <div>
            <label className="mb-2 block text-sm font-bold">Kartennummer</label>
            <div className="rounded-lg border border-slate-600 bg-slate-700/50 p-4">
              <CardNumberElement
                options={{
                  style: {
                    base: { color: '#e2e8f0', fontSize: '16px' },
                    invalid: { color: '#ef4444' },
                  },
                }}
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-bold">Ablaufdatum (MM/YY)</label>
            <div className="rounded-lg border border-slate-600 bg-slate-700/50 p-4">
              <CardExpiryElement
                options={{
                  style: {
                    base: { color: '#e2e8f0', fontSize: '16px' },
                    invalid: { color: '#ef4444' },
                  },
                }}
              />
            </div>
          </div>
        </div>

        <p className="mb-4 text-xs text-slate-400">Nur diese Felder sind erforderlich. Eine Pruefziffer ist nicht noetig.</p>

        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={onBack}
            className="rounded-lg border-2 border-slate-600 px-6 py-3 transition hover:border-slate-500"
          >
            ← Zurueck
          </button>
          <button
            type="button"
            onClick={handlePaymentClick}
            disabled={loading || !stripe}
            className="rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 py-3 font-bold transition-all hover:from-purple-700 hover:to-pink-700 disabled:opacity-50"
          >
            {loading ? 'Wird verarbeitet...' : `EUR ${total} zahlen`}
          </button>
        </div>
      </div>
    </motion.form>
  );
}

function PayPalPaymentForm({ packageId, pkg, formData, voucher, basePrice, total, loading, setLoading, onBack, onWaitlist }) {
  const handlePayPalPayment = async () => {
    setLoading(true);
    try {
      const res = await axios.post('/api/payments/pending', {
        packageId,
        amount: basePrice,
        voucherCode: voucher?.code || null,
        voucherDiscount: voucher?.discount_value || 0,
        finalAmount: basePrice - (voucher?.discount_value || 0),
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        address: formData.address,
        city: formData.city,
        postalCode: formData.postalCode,
        paymentMethod: 'paypal',
      });

      onWaitlist({
        email: formData.email,
        firstName: formData.firstName,
        package: pkg.name,
        voucherCode: res.data.voucherCode,
      });
    } catch (error) {
      alert(`Fehler: ${error.message}`);
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="rounded-lg border border-purple-500/30 bg-slate-800/50 p-6">
        <h2 className="mb-4 text-2xl font-bold">PayPal-Bestaetigung</h2>

        <div className="mb-6 rounded-lg bg-slate-700/50 p-6">
          <div className="mb-2 text-lg font-bold">Zahlungsbestaetigung</div>
          <div className="mb-4 text-slate-300">
            Bestaetige deine Zahlung via PayPal. Du wirst anschliessend weitergeleitet.
          </div>
          <div className="space-y-2 text-sm text-slate-400">
            <div>{formData.email}</div>
            <div>Gesamtbetrag: EUR {total}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={onBack}
            className="rounded-lg border-2 border-slate-600 px-6 py-3 transition hover:border-slate-500"
          >
            ← Zurueck
          </button>
          <button
            onClick={handlePayPalPayment}
            disabled={loading}
            className="rounded-lg bg-blue-600 py-3 font-bold text-white transition-all hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Wird verarbeitet...' : 'Zahlung bestaetigen'}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function WaitlistModal({ data, onClose, onNavigate }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="max-w-md rounded-lg border border-purple-500/50 bg-gradient-to-br from-purple-900 to-slate-900 p-8"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">Fast geschafft!</h2>
          <button onClick={onClose} className="text-slate-400 transition hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="mb-6 space-y-4 text-slate-200">
          <p className="text-base">
            Danke fuer dein Interesse an <span className="font-bold">{data.package}</span>.
          </p>

          <div className="mb-4 rounded-lg border border-yellow-500/30 bg-slate-800/50 p-4">
            <p className="text-sm mb-2">
              Aufgrund der hohen Nachfrage ueberarbeiten wir momentan unsere Infrastruktur. Wir nehmen derzeit keine neuen User auf.
            </p>
          </div>

          <div className="mb-4 rounded-lg border border-amber-500/50 bg-amber-900/30 p-3">
            <p className="text-xs leading-relaxed text-amber-200">
              <strong>Dein Konto wird NICHT belastet.</strong> Es findet keine Abbuchung statt. Deine Zahlungsdaten werden weder gespeichert noch verwendet.
            </p>
          </div>

          <div className="rounded-lg border border-green-500/50 bg-gradient-to-r from-green-900 to-emerald-900 p-4">
            <p className="mb-2 text-sm font-bold">50% Rabatt-Gutschein</p>
            <p className="mb-3 text-xs text-slate-300">
              Du erhaeltst per E-Mail einen exklusiven Gutschein mit 50% Rabatt:
            </p>
            <div className="break-all rounded bg-slate-900 p-3 font-mono text-sm text-green-400">
              {data.voucherCode}
            </div>
            <p className="mt-2 text-xs text-slate-400">
              Dieser Gutschein ist einmalig pro Paket anwendbar.
            </p>
          </div>

          <div className="rounded-lg bg-slate-800/50 p-4">
            <p className="mb-2 text-sm">Benachrichtigungen</p>
            <p className="text-xs text-slate-400">
              Du erhaeltst eine E-Mail an <span className="font-bold text-slate-200">{data.email}</span>, sobald:
            </p>
            <ul className="ml-2 mt-2 space-y-1 text-xs text-slate-400">
              <li>Dein Gutschein aktiv ist</li>
              <li>Neue Kontingente verfuegbar sind</li>
              <li>Du dein Paket aktivieren kannst</li>
            </ul>
          </div>
        </div>

        <button
          onClick={onNavigate}
          className="w-full rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 py-3 font-bold text-white transition-all hover:from-purple-700 hover:to-pink-700"
        >
          Startseite
        </button>
      </motion.div>
    </motion.div>
  );
}
